import { useEffect, useRef, useState } from 'react';

const features = [
  { icon: 'ri-time-line', text: 'Belirli süre sonra akıllı popup tetiklenir' },
  { icon: 'ri-gift-line', text: 'Fırsat listesine özel kampanyalar gönderilir' },
  { icon: 'ri-phone-line', text: 'Telefon numarası toplanır, takip başlar' },
];

export default function LeadSection() {
  const [visible, setVisible] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) return;
    setLoading(true);
    try {
      const body = new URLSearchParams();
      body.append('name', name);
      body.append('phone', phone);
      await fetch('https://readdy.ai/api/form/d781u8hrcamcd36darug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="lead-toplama" className="relative py-24 md:py-32 bg-[#0a0a0f] overflow-hidden">
      {/* Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-[#00d9ff]/8 rounded-full blur-[150px] pointer-events-none" />

      <div ref={ref} className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left */}
          <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <span className="inline-block text-[#d4af37] text-xs font-bold uppercase tracking-[4px] mb-4">
              Akıllı Lead Toplama
            </span>
            <h2 className="text-3xl md:text-5xl font-black text-white leading-tight mb-6">
              Her Ziyaretçi{' '}
              <span style={{ background: 'linear-gradient(135deg, #00d9ff, #d4af37)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                Hemen Yazmaz
              </span>
            </h2>
            <p className="text-white/50 text-base leading-relaxed mb-8">
              Ziyaretçilerin büyük çoğunluğu ilk ziyarette iletişime geçmez. Akıllı lead toplama sistemi, bu ziyaretçileri kaybetmeden yakalar.
            </p>

            <div className="flex flex-col gap-5">
              {features.map((f, i) => (
                <div
                  key={i}
                  className={`flex items-start gap-4 transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
                  style={{ transitionDelay: `${i * 120 + 200}ms` }}
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#00d9ff]/10 shrink-0">
                    <i className={`${f.icon} text-xl text-[#00d9ff]`}></i>
                  </div>
                  <p className="text-white/70 text-base leading-relaxed pt-1.5">{f.text}</p>
                </div>
              ))}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 mt-10">
              {[
                { val: '%47', label: 'Daha Fazla Lead' },
                { val: '3.2x', label: 'Dönüşüm Artışı' },
              ].map((s, i) => (
                <div
                  key={i}
                  className="rounded-xl p-4 border border-white/10"
                  style={{ background: 'rgba(26,26,46,0.6)', backdropFilter: 'blur(20px)' }}
                >
                  <div
                    className="text-2xl font-black mb-1"
                    style={{ background: 'linear-gradient(135deg, #d4af37, #f4d03f)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}
                  >
                    {s.val}
                  </div>
                  <div className="text-white/40 text-xs uppercase tracking-widest">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Form Card */}
          <div
            className={`transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
          >
            <div
              className="rounded-3xl p-8 md:p-10 border-2 border-[#d4af37]/30 relative overflow-hidden"
              style={{
                background: 'rgba(26, 26, 46, 0.8)',
                backdropFilter: 'blur(40px)',
                boxShadow: '0 0 60px rgba(212,175,55,0.1)',
              }}
            >
              {/* Top glow */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-1 bg-gradient-to-r from-transparent via-[#d4af37] to-transparent rounded-full" />

              {!submitted ? (
                <>
                  <div className="text-center mb-8">
                    <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#d4af37]/15 mx-auto mb-4">
                      <i className="ri-gift-2-line text-3xl text-[#d4af37]"></i>
                    </div>
                    <h3 className="text-white font-black text-xl md:text-2xl mb-2">
                      Özel kampanyalardan ilk siz haberdar olun
                    </h3>
                    <p className="text-white/50 text-sm">
                      Telefon numaranızı bırakın, size özel fırsatları kaçırmayın
                    </p>
                  </div>

                  <form
                    data-readdy-form
                    id="firsat-listesi-form"
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-4"
                  >
                    <div className="relative">
                      <i className="ri-user-line absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-base"></i>
                      <input
                        type="text"
                        name="name"
                        placeholder="Ad Soyad"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        className="w-full h-14 pl-11 pr-4 rounded-xl bg-white/5 border border-white/10 focus:border-[#00d9ff]/60 text-white text-sm placeholder-white/30 outline-none transition-colors"
                      />
                    </div>
                    <div className="relative">
                      <i className="ri-phone-line absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-base"></i>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="Telefon Numarası"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        required
                        className="w-full h-14 pl-11 pr-4 rounded-xl bg-white/5 border border-white/10 focus:border-[#00d9ff]/60 text-white text-sm placeholder-white/30 outline-none transition-colors"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full h-16 rounded-xl bg-gradient-to-r from-[#d4af37] to-[#f4d03f] text-black font-black text-base hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-70"
                    >
                      {loading ? 'Gönderiliyor...' : 'Fırsat Listesine Katıl'}
                    </button>
                  </form>

                  <div className="flex items-center justify-center gap-2 mt-4">
                    <i className="ri-lock-line text-white/30 text-sm"></i>
                    <span className="text-white/30 text-xs">Bilgileriniz güvende, spam göndermiyoruz</span>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-400/15 mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-green-400"></i>
                  </div>
                  <h3 className="text-white font-black text-xl mb-2">Harika! Listeye Eklendiniz</h3>
                  <p className="text-white/50 text-sm">Özel kampanyalar için sizi arayacağız.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
